import { Fragment, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Icon, Spinner } from "../../atoms";
import { ExclamationTriangleIcon, CheckIcon } from "@heroicons/react/20/solid";
import { Transition } from "@headlessui/react";
import { CreditBoxStatus } from "@/entities/CreditBox/CreditBox.types";

const DEFAULT_TEXT = {
    [CreditBoxStatus.Running]:
        "Gathering data. This process should only take a couple of minutes. Please wait...",
    [CreditBoxStatus.Success]: "Success!",
    [CreditBoxStatus.Failure]:
        "Error: data mapping is still processing. Please try again later and contact us if the error persists",
};

type ProgressDownloadProps = {
    isPolling: boolean;
    isFailure: boolean;
    isSuccess: boolean;
    text?: string;
    title?: string;
    onCancel?: () => void;
    onClose?: () => void;
    testId?: string;
    className?: string;
};

export function ProgressDownload({
    isPolling,
    isFailure,
    isSuccess,
    text,
    title = "Download",
    onCancel,
    onClose,
    testId,
    className,
}: ProgressDownloadProps) {
    const [show, setShow] = useState(false);
    const [state, setState] = useState<CreditBoxStatus>(
        CreditBoxStatus.Pending,
    );
    const [displayText, setDisplayText] = useState("");

    const handleClose = useCallback(() => {
        setShow(false);
        setState(CreditBoxStatus.Pending);
        setDisplayText("");
        onClose?.();
    }, [onClose]);

    const handleCancel = useCallback(() => {
        setShow(false);
        setState(CreditBoxStatus.Pending);
        setDisplayText("");
        onCancel?.();
    }, [onCancel]);

    useEffect(() => {
        if (isPolling) {
            setState(CreditBoxStatus.Running);
            setShow(true);
        } else if (isFailure) {
            setState(CreditBoxStatus.Failure);
            setShow(true);
        } else if (isSuccess) {
            setState(CreditBoxStatus.Success);
            setShow(true);
        }
    }, [isPolling, isSuccess, isFailure]);

    useEffect(() => {
        if (state !== CreditBoxStatus.Pending) {
            setDisplayText(text || DEFAULT_TEXT[state]);
        }
    }, [state, text]);

    useEffect(() => {
        if (state === CreditBoxStatus.Success) {
            const timer = setTimeout(() => {
                handleClose();
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [state, handleClose]);

    const isRunning = state === CreditBoxStatus.Running;
    const isComplete =
        state === CreditBoxStatus.Success || state === CreditBoxStatus.Failure;

    return (
        <div data-testid={testId}>
            <Transition
                as={Fragment}
                show={show}
                enter="transform transition duration-[600ms]"
                enterFrom="translate-y-[200px]"
                enterTo="translate-y-0"
                leave="transform transition duration-[600ms]"
                leaveFrom="translate-y-2"
                leaveTo="translate-y-[200px]"
            >
                <div
                    className={clsx(
                        "w-full",
                        "max-w-[468px]",
                        "bg-gray-700",
                        "rounded-[8px]",
                        "border",
                        "border-default",
                        "p-6",
                        "fixed",
                        "z-50",
                        "bottom-[32px]",
                        "right-[32px]",
                        "shadow-default",
                        className,
                    )}
                >
                    <div className="flex items-center justify-between">
                        <h5 className="heading-m">{title}</h5>
                        <div className="flex items-center">
                            {isRunning && onCancel && (
                                <button
                                    className="secondary"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                            )}
                            {isComplete && (
                                <Icon.Close
                                    className="ml-5 cursor-pointer"
                                    onClick={handleClose}
                                />
                            )}
                        </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                        <p
                            className={clsx(
                                "subdued ui-element mr-2 overflow-hidden text-ellipsis",
                            )}
                        >
                            {displayText}
                        </p>
                        <div className="flex">
                            {state === CreditBoxStatus.Failure && (
                                <ExclamationTriangleIcon
                                    data-testid="polling-error-icon"
                                    fill="#FF8A8A"
                                    width={16}
                                />
                            )}
                            {state === CreditBoxStatus.Success && (
                                <CheckIcon
                                    data-testid="polling-success-icon"
                                    fill="#72C681"
                                    width={16}
                                />
                            )}
                            {isRunning && <Spinner />}
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
}
